import { MAX_INPUT_LENGTH } from '../store/mfsDataConstraints';
import { hasAnyElement, hasProperty, isNullOrWhitespace, isString } from '../utility';
import { mfsError } from './storeErrors';

import { add, MfsDeltaValue, MfsValue, remove } from '../store/mfsValue';
import { isValidLabelPropertyValue } from '../store/labels';
import { DISPLAY_TEXT_KEY, SECONDARY_LABELS_KEY } from '../utility';
import { isMfsDeltaValue } from '../store/mfsValueTypeGuards';
import { AppDataOf, MfsItem } from '..';

/** Ensures that the value is a non-empty string value that doesn't exceed size limits. */
export function throwIfInvalidItemId(argumentName: string, value: unknown): void {
  if (!isString(value)) {
    throw mfsError.invalidItemId();
  }
  throwIfEmptyOrTooLong(argumentName, value);
}

/** Throws if `values` is empty or contains any null, whitespace or empty string values. */
export function throwIfEmptyValues(argumentName: string, values: string[]): void {
  if (!hasAnyElement(values) || values.some((id) => isNullOrWhitespace(id))) {
    throw mfsError.emptyValues(argumentName);
  }
}

/**
 * If the given `value` is a boolean, throws when it's null or undefined; otherwise throws when the `value` is falsy.
 *
 * @param value The value to check if empty.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export function throwIfEmptyValue<T extends unknown>(argumentName: string, value: T | undefined): void {
  if (typeof value === 'boolean' && value === false) {
    return;
  }

  if (!value || (typeof value === 'string' && isNullOrWhitespace(value)) || value == 0) {
    throw mfsError.emptyValues(argumentName);
  }
}

/** Throws new validation error if the input exceeds the maximum input length. */
export function throwIfTooLong(argumentName: string, input: string): void {
  if (typeof input === 'string' && isTooLong(input)) {
    throw mfsError.maxInputLengthExceeded(argumentName);
  }
}

/** Whether the `input` string exceeds max length. */
export const isTooLong = (input: string): boolean => input.length >= MAX_INPUT_LENGTH;

/** Throws new validation error if the input is empty, null, whitespace string or exceeds the maximum input length. */
export function throwIfEmptyOrTooLong(argumentName: string, argumentValue: string): void {
  throwIfEmptyValue(argumentName, argumentValue);
  throwIfTooLong(argumentName, argumentValue);
}

/**
 * Throws new validation error if the input has values that are empty, null, whitespace string or exceeds the maximum
 * input length.
 */
export function throwIfHasEmptyOrTooLongValues(argumentName: string, argValue: string[] | undefined): void {
  if (argValue !== undefined) {
    argValue.some((value) => throwIfEmptyOrTooLong(argumentName, value));
  }
}

/** Validates the given property name */
export function throwIfInvalidPropertyName(propertyName: string): void {
  throwIfEmptyOrTooLong('propertyName', propertyName);
}

/** @ignore */
export function throwIfInvalidDisplayText(propertyName: string, value: MfsValue): void {
  if (propertyName === DISPLAY_TEXT_KEY && !isValidDisplayTextValue(value)) {
    throw mfsError.invalidDisplayText();
  }
}

/** Throws if the given value or delta values have any invalid secondary labels. */
export function throwIfInvalidSecondaryLabels(propertyName: string, value: MfsValue | MfsDeltaValue): void {
  if (propertyName !== SECONDARY_LABELS_KEY) {
    return;
  }

  if (isMfsDeltaValue(value)) {
    if (!isValidDeltaLabels(value)) {
      throw mfsError.invalidSecLabelsDelta();
    }
    return;
  }

  if (propertyName === SECONDARY_LABELS_KEY && !isValidSecondaryLabelsValue(value)) {
    throw mfsError.invalidSecLabel();
  }
}

/** @ignore */
export function throwIfDeltaUpdatingSingleValueProperty(propertyName: string, value: MfsValue | MfsDeltaValue): void {
  if (isMfsDeltaValue(value)) {
    // check for mutable, single-valued, known properties.
    if (propertyName === 'labelId' || propertyName === 'displayText') {
      throw mfsError.deltaNotAllowed(propertyName);
    }
  }
}

// Should be a defined string value.
const isValidDisplayTextValue = (value: MfsValue): boolean =>
  !!value && typeof value === 'string' && !isNullOrWhitespace(value);

// should be an array of valid label values or undefined.
const isValidSecondaryLabelsValue = (value: MfsValue): boolean =>
  value === undefined || (Array.isArray(value) && !value.some((v) => !isString(v) || !isValidLabelPropertyValue(v)));

const isValidDeltaLabels = (value: MfsDeltaValue) => {
  const hasAnyInvalidLabelValue = (x: MfsValue[]) => x.some((v) => !v || !isString(v) || !isValidLabelPropertyValue(v));

  const addValues = value[add];
  const removeValues = value[remove];

  return (
    (addValues || removeValues) &&
    (!addValues || !hasAnyInvalidLabelValue(addValues)) &&
    (!removeValues || !hasAnyInvalidLabelValue(removeValues))
  );
};

/** @ignore */
export function throwIfInvalidDisplayTextInPatch(patch: Partial<AppDataOf<MfsItem>>): void {
  if (hasProperty(patch, DISPLAY_TEXT_KEY) && !isValidDisplayTextValue(patch?.displayText)) {
    throw mfsError.invalidDisplayText();
  }
}

/** @ignore */
export function throwIfInvalidSecondaryLabelsInPatch(patch: Partial<AppDataOf<MfsItem>>): void {
  if (hasProperty(patch, SECONDARY_LABELS_KEY) && !isValidSecondaryLabelsValue(patch?.secondaryLabels)) {
    throw mfsError.invalidSecLabel();
  }
}

/** Asserts the given condition & throws if false */
export function assertPrecondition(condition: boolean, errorOrMessage?: () => Error): asserts condition {
  if (!condition) {
    throw typeof errorOrMessage === 'function' ? errorOrMessage() : mfsError.assertionFailed();
  }
}
